import React, { useState, useContext } from "react"
import styled, { css } from "styled-components"
import { Link } from "gatsby"
import {
  FacebookShareButton,
  FacebookIcon,
  LinkedinIcon,
  LinkedinShareButton,
} from "react-share"
import { CopyToClipboard } from "react-copy-to-clipboard"
import Ellipse from "@images/knowledge-center/ellipse.svg"
import { GlobalStateContext } from "@context/GlobalContextProvider"
import {
  CategoryColorLightFinder,
  CategoryBadgeHoverColor,
  CategoryTitleString,
} from "@utils/knowledge-center"

const size = {
  tablet: 950,
  mobile: 600,
}

const above = Object.keys(size).reduce((acc, label) => {
  acc[label] = (...args) => css`
    @media (min-width: ${size[label]}px) {
      ${css(...args)};
    }
  `
  return acc
}, {})

const ArticleHeaderWrapper = styled.div`
  padding: 32px 24px 8px;
  ${above.mobile`
  padding:48px 48px 16px;
  `}

  ${above.tablet`
      max-width: 856px;
    padding: 64px 48px 16px;
    margin: 0 auto ;
  `}
`

const ArticleHeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  ${above.mobile`
  margin-bottom:0;
  align-items:center;
  `}
`

const CategoryBadgeButton = styled(Link)`
  font-weight: 900;
  font-size: 12px;
  line-height: 15px;
  color: #2d3748;
  text-decoration: none;
  padding: 6.5px 8px;
  letter-spacing: 0.1em;
  margin-bottom: 8px;

  ${above.mobile`
    margin-right:16px;
    margin-bottom:0;
    `}

  &:hover {
    text-decoration: none;
    background: ${props => props.categoryBadgeHoverColor};
  }
`
const SocialIconsContainer = styled.div`
  display: flex;
  button {
    height: 28px;
    width: 28px;
    margin-right: 12px;
    &:last-of-type {
      margin-right: 0;
    }
  }

  svg {
    height: 28px;
    width: 28px;
    &:hover {
      border: none;
      border-radius: 50%;
      circle {
        fill: #3c9dfd;
        stroke: white;
      }
      rect {
        fill: #2d3748;
      }
    }
  }

  .react-share__ShareButton {
    svg {
      border: 1px solid #2d3748;
      border-radius: 50%;
      padding: 0;
      circle {
        fill: white;
      }
      path {
        fill: #2d3748;
      }
      &:hover {
        border: none;
        circle {
          fill: #3c9dfd;
        }
        path {
          fill: #2d3748;
        }
      }
    }
  }
`

const TimeToReadDateContainer = styled.div`
  display: flex;
  align-items: center;

  p {
    color: var(--grey);
    font-size: 14px;
    line-height: 17.6px;
    margin-bottom: 0;
    margin-right: 8px;
    &:last-of-type {
      margin-right: 0;
    }
  }
  img {
    margin-right: 8px;
    width: 2px;
    height: 2px;
  }
`

const CategoryTTRDateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  .category-badge-button {
    background: ${props => props.categoryBadgeColor};
    &:hover {
      background: ${props => props.categoryBadgeHoverColor};
    }
  }
  ${above.mobile`
flex-direction:row;
align-items:center;
`}
`

const InvisibleButton = styled.button`
  width: 100%;
  background: none;
  color: inherit;
  border: none;
  padding: 0;
  font: inherit;
  cursor: pointer;
  outline: inherit;
  position: relative;
  &:hover svg {
    border: none;
    border-radius: 50%;

    circle {
      fill: #3c9dfd;
      stroke: white;
    }
    rect {
      fill: #2d3748;
    }
  }
  .tooltip-text {
    min-width: 70px;
    font-size: 12px;
    line-height: 15.08px;
    padding: 2px 4px;
    background-color: #2d3748;
    color: white;
    border-radius: 2px;
    position: absolute;
    right: 0;
    top: -23px;
    visibility: hidden;
  }
  &:hover .tooltip-text {
    visibility: visible;
  }
  svg {
    height: 28px;
    width: 28px;
  }
`

const ArticleHeader = ({ articleData, bankerData }) => {
  const CategorySlug = () => {
    if (articleData.category === "finances") {
      return "financial-wellness"
    }
    if (articleData.category === "mortgages") {
      return "mortgage-matters"
    }
    if (articleData.category === "homeownership") {
      return "house-to-home"
    }
  }
  const shareLink = bankerData
    ? `https://www.atlanticbay.com/${bankerData?.slug}/knowledge-center/${articleData.slug}/`
    : `https://www.atlanticbay.com/knowledge-center/${articleData.slug}/`
  const baseSlug = bankerData
    ? `${bankerData?.slug}/knowledge-center`
    : "knowledge-center"

  const [linkCopied, setLinkCopied] = useState(false)
  const [tooltipText, setTooltipText] = useState("Copy Link")
  return (
    <ArticleHeaderWrapper>
      <ArticleHeaderContainer>
        <CategoryTTRDateContainer
          categoryBadgeColor={CategoryColorLightFinder(articleData.category)}
          categoryBadgeHoverColor={CategoryBadgeHoverColor(
            articleData.category
          )}
        >
          <CategoryBadgeButton
            className="category-badge-button"
            to={`/${baseSlug}/${CategorySlug()}/`}
          >
            {CategoryTitleString(articleData.category)}
          </CategoryBadgeButton>
          <TimeToReadDateContainer>
            <p>{`${articleData.body.childMarkdownRemark.timeToRead} min read`}</p>
            <img src={Ellipse} alt="ellipse icon" />
            <p>{articleData.date}</p>
          </TimeToReadDateContainer>
        </CategoryTTRDateContainer>
        <SocialIconsContainer>
          <FacebookShareButton className="facebook" url={shareLink}>
            <FacebookIcon round />
          </FacebookShareButton>
          <LinkedinShareButton url={shareLink}>
            <LinkedinIcon round />
          </LinkedinShareButton>
          <CopyToClipboard text={shareLink}>
            <InvisibleButton
              className="copy-link"
              onClick={() => {
                setLinkCopied(true)
                setTooltipText("Link copied!")
              }}
              linkCopied={linkCopied}
              onMouseLeave={() => {
                setLinkCopied(false)
                setTooltipText("Copy Link")
              }}
            >
              <span className="tooltip-text">{tooltipText}</span>
              <svg
                width="28"
                height="28"
                viewBox="0 0 28 28"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="14" cy="14" r="13.5" stroke="#2D3748" />
                <g clipPath="url(#clip0)">
                  <path
                    d="M13.0273 14.4862C13.2361 14.7653 13.5024 14.9962 13.8083 15.1633C14.1142 15.3304 14.4524 15.4298 14.8 15.4547C15.1477 15.4796 15.4966 15.4294 15.8231 15.3076C16.1497 15.1858 16.4462 14.9952 16.6926 14.7487L18.151 13.2904C18.5937 12.8319 18.8387 12.218 18.8331 11.5807C18.8276 10.9434 18.572 10.3338 18.1213 9.88316C17.6707 9.43251 17.0611 9.17689 16.4238 9.17136C15.7865 9.16582 15.1726 9.41081 14.7141 9.85355L13.878 10.6848"
                    stroke="#2D3748"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M14.9709 13.5141C14.7622 13.235 14.4958 13.0041 14.19 12.8369C13.8841 12.6698 13.5459 12.5705 13.1983 12.5456C12.8506 12.5207 12.5017 12.5708 12.1751 12.6927C11.8486 12.8145 11.5521 13.0051 11.3057 13.2516L9.84732 14.7099C9.40458 15.1683 9.15959 15.7823 9.16513 16.4196C9.17067 17.0568 9.42629 17.6665 9.87693 18.1171C10.3276 18.5677 10.9372 18.8234 11.5745 18.8289C12.2118 18.8344 12.8257 18.5895 13.2841 18.1467L14.1154 17.3155"
                    stroke="#2D3748"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </g>
                <defs>
                  <clipPath id="clip0">
                    <rect
                      width="11.6667"
                      height="11.6667"
                      fill="white"
                      transform="translate(8.1665 8.16675)"
                    />
                  </clipPath>
                </defs>
              </svg>
            </InvisibleButton>
          </CopyToClipboard>
        </SocialIconsContainer>
      </ArticleHeaderContainer>
    </ArticleHeaderWrapper>
  )
}

export default ArticleHeader
